import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import GlassCard from "@/components/common/GlassCard";
import StatusBadge from "@/components/common/StatusBadge";
import CrudDrawer from "@/components/common/CrudDrawer";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

export default function Users() {
  const [items, setItems] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [u, r] = await Promise.all([api.get("/users"), api.get("/roles")]);
      setItems(u.data || []);
      setRoles(r.data?.roles || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const save = async (values) => {
    try {
      if (editing) {
        const { password, ...rest } = values;
        await api.patch(`/users/${editing.id}`, password ? { ...rest, password } : rest);
      } else {
        await api.post("/auth/register", values);
      }
      setDrawer(false);
      fetchAll();
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const confirmRemove = async () => {
    if (!pendingDelete) return;
    try {
      await api.delete(`/users/${pendingDelete.id}`);
      setPendingDelete(null);
      fetchAll();
      toast.success("Removed");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const fields = [
    { key: "name", label: "Name", type: "text" },
    { key: "email", label: "Email", type: "text" },
    ...(!editing ? [{ key: "password", label: "Password", type: "text", hint: "Min 8 chars" }] : [{ key: "password", label: "New password (optional)", type: "text" }]),
    { key: "role", label: "Role", type: "select", options: roles.map(r => ({ label: r.name, value: r.slug })) },
    { key: "status", label: "Status", type: "select", options: [
      { label: "Active", value: "active" }, { label: "Disabled", value: "disabled" }
    ]},
  ];

  return (
    <div>
      <PageHeader
        title="Users"
        subtitle="Manage admin accounts and role assignments."
        actions={
          <Button onClick={() => { setEditing(null); setDrawer(true); }} className="btn-gradient text-white border-0">
            <Icons.UserPlus className="w-4 h-4 mr-1.5" /> Invite user
          </Button>
        }
      />

      <GlassCard className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-white/45 bg-white/[0.02]">
                <th className="px-5 py-3 font-medium">User</th>
                <th className="px-5 py-3 font-medium">Role</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Last login</th>
                <th className="px-5 py-3 font-medium text-right w-24">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar className="w-8 h-8 bg-gradient-to-br from-[#8B5CF6] to-[#3B82F6]">
                        <AvatarFallback className="text-xs bg-transparent text-white">
                          {(u.name || u.email || "U").split(" ").map(s => s[0]).join("").slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <div className="text-white text-sm truncate">{u.name || "—"}</div>
                        <div className="text-white/50 text-xs truncate">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-white/80 capitalize">{(u.role || "").replace("_", " ")}</td>
                  <td className="px-5 py-3"><StatusBadge value={u.status || "active"} /></td>
                  <td className="px-5 py-3 text-white/50 text-xs">{u.last_login ? new Date(u.last_login).toLocaleString() : "Never"}</td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => { setEditing(u); setDrawer(true); }} className="p-1.5 rounded hover:bg-white/5 text-white/60 hover:text-white">
                      <Icons.Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => setPendingDelete(u)} className="p-1.5 rounded hover:bg-white/5 text-white/60 hover:text-red-300 ml-1">
                      <Icons.Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {!loading && items.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-12 text-center text-white/40">No users yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </GlassCard>

      <CrudDrawer
        open={drawer}
        onOpenChange={setDrawer}
        title={editing ? "Edit user" : "Invite user"}
        fields={fields}
        initial={editing || { role: "editor", status: "active" }}
        onSave={save}
        resourceKey="users"
      />

      <AlertDialog open={!!pendingDelete} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent className="glass-strong border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this user?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              {pendingDelete?.email} will lose access to the admin panel.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-white">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemove} className="bg-red-600 hover:bg-red-500 text-white">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
